/**
 * Central visible labels — the ONE place a role, assignment, route activity,
 * customer-facing stage or tag/status word is spelled out for the UI.
 *
 * Now backed by react-i18next: every label function calls `i18next.t()` so
 * the displayed string tracks the active language automatically.
 *
 * API identifiers stay unchanged (`admin / staff / order_desk / driver / cleaner`
 * and `pickup / cleaning / delivery`).
 */
import i18next from 'i18next';

const t = (key, fallback) => {
    const result = i18next.t(key);
    return result !== key ? result : (fallback || key);
};

export const ROLE_LABELS = new Proxy({}, {
    get: (_, role) => t(`roles.${role}`, String(role)),
});

export const ROLE_DESCRIPTIONS = new Proxy({}, {
    get: (_, role) => t(`roleDescriptions.${role}`, ''),
});

export const ASSIGNABLE_ROLES = ['admin', 'staff', 'order_desk', 'driver', 'cleaner'];

export const ASSIGNMENT_ROLE_FILTER = {
    pickup: ['driver'],
    cleaning: ['cleaner', 'driver'],
    delivery: ['driver'],
};

export const ASSIGNMENT_LABELS = new Proxy({}, {
    get: (_, slot) => t(`assignments.${slot}`, String(slot)),
});

export const ACTIVITY_LABELS = new Proxy({}, {
    get: (_, activity) => t(`activities.${activity}`, String(activity)),
});

export const ROUTE_STOP_STATUS_LABELS = new Proxy({}, {
    get: (_, status) => t(`routeStopStatus.${status}`, String(status)),
});

export const CUSTOMER_STAGE_LABELS = new Proxy({}, {
    get: (_, stage) => t(`customerStages.${stage}`, String(stage)),
});

export const DOCUMENT_LABELS = new Proxy({}, {
    get: (_, type) => t(`documents.${type}`, String(type)),
});

export const TAG_STATUS_LABELS = new Proxy({}, {
    get: (_, status) => t(`tagStatus.${status}`, String(status)),
});

export const MISC_LABELS = new Proxy({}, {
    get: (_, key) => t(`misc.${key}`, String(key)),
});

const NOTIFICATION_KEY_MAP = {
    'unpicked job': 'unpickedJob',
    'excessive pickup': 'excessivePickup',
    'not started cleaning': 'notStartedCleaning',
    'excessive cleaning': 'excessiveCleaning',
    'not started dispatch': 'notStartedDispatch',
    'excessive dispatch': 'excessiveDispatch',
    'invoice overdue': 'invoiceOverdue',
    'reschedule needed': 'rescheduleNeeded',
    'job assigned pickup': 'jobAssignedPickup',
    'job assigned cleaning': 'jobAssignedCleaning',
    'job assigned delivery': 'jobAssignedDelivery',
    'job cancelled': 'jobCancelled',
    'job completed': 'jobCompleted',
    'job paid': 'jobPaid',
    'job note added': 'jobNoteAdded',
    'delete request': 'deleteRequest',
    'job delete approved': 'jobDeleteApproved',
    'client intake received': 'clientIntakeReceived',
    'reminder due': 'reminderDue',
};

export const NOTIFICATION_TITLES = new Proxy({}, {
    get: (_, key) => {
        const mapped = NOTIFICATION_KEY_MAP[key];
        return mapped ? t(`notifications.${mapped}`, key) : key;
    },
});

const norm = (v) => String(v ?? '').trim().toLowerCase().replace(/\s+/g, '_');

/** "admin" | "Admin" | "Order Desk" | "order_desk" → visible label. */
export const roleLabel = (role) => ROLE_LABELS[norm(role)] || String(role ?? '');

/** "Admin, Driver" (the API joins roles with a comma) → "Administrator, Driver". */
export const roleListLabel = (roles) =>
    String(roles ?? '').split(',').map((r) => r.trim()).filter(Boolean).map(roleLabel).join(', ');

export const assignmentLabel = (slot) => ASSIGNMENT_LABELS[norm(slot)] || String(slot ?? '');
export const activityLabelOf = (activity) => ACTIVITY_LABELS[String(activity ?? '').toUpperCase()] || String(activity ?? '');
export const routeStopStatusLabel = (status) => ROUTE_STOP_STATUS_LABELS[String(status ?? '').toUpperCase()] || String(status ?? '');
export const customerStageLabel = (stage) => CUSTOMER_STAGE_LABELS[String(stage ?? '').toUpperCase()] || String(stage ?? '');
export const documentLabel = (type) => DOCUMENT_LABELS[String(type ?? '').toUpperCase()] || String(type ?? '');
export const tagStatusLabel = (status) => TAG_STATUS_LABELS[String(status ?? '').toUpperCase()] || String(status ?? '');
export const notificationTitle = (key) => NOTIFICATION_TITLES[key] || key;

/** Visible tag text: yearly code ("26-00001") when it exists, else the legacy number. */
export const tagLabel = (badge) => {
    if (!badge) return '';
    if (typeof badge === 'string' || typeof badge === 'number') return String(badge);
    return badge.code || (badge.badgeNo != null ? String(badge.badgeNo) : '');
};
